import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from '@hooks/useTranslations';
import { Redux } from 'src/redux/redux.interface';
import TrimSelectorDuck from '../redux/trimSelector/trimSelector.duck';
import FinanceWidgetDuck from '../redux/financeWidget/financeWidget.duck';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { useJourneyType } from '@hooks/useJourneyType';
import { useRouter } from 'next/router';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST, IFinanceWidgetButtonType } from '../context/featureSwitchApp';
import { CarJourneyType } from '../services';
import { useFeatureSwitch } from '@hooks/useFeatureSwitch';
import { useDateUtils } from '@utils/Date.utils';
import { ICalculateSummaryRequestData } from '../services/calculateSummary/calculateSummary.types';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import { useState } from 'react';
import { usePrices } from '@hooks/usePriceCalculator';
import routes from '../constants/routes';
import Routes from '../constants/routes';
import {
    BACKEND_DEEPLINK_REDIRECT_URL,
    BRAND,
    FINANCE_DETAIL_VIEW,
    isBrandAC,
    isBrandAP,
    isBrandDS,
    isBrandOV,
    isMarketGB,
    LANGUAGE,
} from '../constants/main';
import { Icons } from '@components/Icon';
import { getFinanceDetailText, getFinanceDetailView, getMonthlyPriceObjByType } from '@utils/Price.utils';
import { StockFormatedPriceType } from '../services/stock/models/stockItem.price.model';
import { getFormattedOfferValidityDate } from '@utils/Car.utils';
import { usePlausible, UserEvent } from '@hooks/usePlausible';
import { useExperiment } from '@hooks/useExperiment';
import { PosthogTrigger } from '../constants/enums';

export const useOrdelPanelLogic = () => {
    const { t } = useTranslations();
    const { formatMoney } = useMoneyFormatter();
    const { pushTrigger, getPageNameByUrl, getStageViewByUrl } = useExperiment();
    const { plausibleUserEvent } = usePlausible();
    const dispatch = useDispatch();
    const filteredStockTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredStockTrims(state));
    const isPriceListLoading = useSelector((state: Redux) => FinanceWidgetDuck.getIsPricesListLoading(state));
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { financeQuote, globalTexts, pricesList } = useFinanceWidgetDuck();
    const { isFinance } = useJourneyType();
    const router = useRouter();
    const { isStockJourney } = useCarStockJourneyCheck();
    const isStockJourneyAllowed = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_ALLOWED_CAR_JOURNEYS,
        CarJourneyType.STOCK
    );
    const {
        isBasketLegalTermsEnabled,
        isSaveBasketCTADisabled,
        isStockBasketSaveItemEnabled,
        isBrandRecommendsJourneyAllowed,
    } = useFeatureSwitch();
    const isAllowedRedirectToStock = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_REDIRECT_TO_STOCK_ENABLED);
    const isAllowedRedirectToMTO = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_REDIRECT_TO_MTO_ENABLED);
    const isCataloguePriceFeatureSwitchEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_DISPLAY_CATALOGUE_PRICE
    );
    const isFinanceWidgetDisabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DISABLED_FINANCE_WIDGET);
    const isCustomDepositTextAllowed = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_CUSTOM_DEPOSIT_TEXT_ALLOWED
    );
    const isBudgetChangeAllowed = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BUDGET_CHANGE_ALLOWED);
    const { FormattedDate } = useDateUtils();

    const params: ICalculateSummaryRequestData = {
        carConfigId: carDetails._id ?? carDetails.externalId,
        token: financeQuote?.token ?? currentDeal?.financeQuoteId,
        ...(isStockJourney && { carConfiguration: carDetails }),
    };

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise(params);
    const [renderWidget, setRenderWidget] = useState<() => void>();

    const { cash, finance, catalog } = usePrices(null, null, null, true, true);

    const currentPath = router.asPath;
    const isBasketPage = currentPath.includes(routes.BASKET);
    const showBasketLegalTerms = isBasketLegalTermsEnabled && isFinance;

    const { totalPrice: totalCashPrice, totalBasicPrice, totalBasicPriceWithoutVat } = cash;
    const {
        totalPrice: totalMonthlyPrice,
        financeProperties: { hasFinanceVariants, deposit, apr },
    } = finance;

    const redirectToStock = () => {
        const nameplateBodyStyleSlug = carDetails.nameplateBodyStyleSlug;
        const modelId = carDetails.model.id;
        const bodyStyleId = carDetails.bodyStyle.id;
        const specPackDesc = carDetails.specPack.description;
        const fuel = carDetails.fuel;

        router.push(
            `${routes.SELECTOR}/${CarJourneyType.STOCK}/${nameplateBodyStyleSlug}/${modelId}/${bodyStyleId}?sort=price%2Casc&grades=${specPackDesc}&energies=${fuel}`
        );
    };

    const scrollToSockSlice = () => {
        const stockSlice = document.getElementById('stock-slice');
        const y = stockSlice.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    const redirectToMTO = () => {
        window.location.href = `${BACKEND_DEEPLINK_REDIRECT_URL}/?brand=${BRAND}&language=${LANGUAGE}&version=${carDetails.lcdv16}&color=${carDetails.exteriorColour.id}&trim=${carDetails.interiorColour.id}&page=config`;
    };

    const handleFinanceWidgetBtnClick = () => {
        if (router?.pathname?.includes(Routes.CAR)) {
            pushTrigger(
                PosthogTrigger.INTERACTION,
                getPageNameByUrl(),
                null,
                null,
                null,
                null,
                null,
                null,
                'open_finance_calculator',
                null,
                null,
                null,
                null,
                getStageViewByUrl()
            );

            plausibleUserEvent(UserEvent.OpenFinanceWidgetFromConfigPage);
        }

        renderWidget && renderWidget();
    };

    const registerRenderCb = (cb: () => void) => {
        setRenderWidget(() => cb);
    };

    const showSaveButton = isSaveBasketCTADisabled
        ? false
        : isStockJourney
        ? isStockBasketSaveItemEnabled
        : !isBrandRecommendsJourneyAllowed;

    const isCataloguePriceEqualToStorePrice = catalog?.totalPrice && catalog?.totalPrice === totalCashPrice;
    const hideCashPayment = !isBudgetChangeAllowed && isFinance && !isMarketGB;

    const infoIconType =
        isBrandAC || isBrandAP || isBrandDS || (isBrandOV && !isMarketGB) ? Icons.InfoCircle : Icons.Info;

    const amountRounding = getMonthlyPriceObjByType(carDetails.prices, StockFormatedPriceType.EMPLOYEE)?.amountRounding;
    const financeDetailView = getFinanceDetailView(carDetails.prices, StockFormatedPriceType.EMPLOYEE);
    const IconName = isBrandAP ? Icons.ChevronRightThin : Icons.ChevronRight;
    const financeDetailText = getFinanceDetailText(
        carDetails.prices,
        StockFormatedPriceType.EMPLOYEE,
        globalTexts,
        pricesList,
        carDetails.externalId || carDetails.vehicleId
    );
    const showOfferValidityDate: boolean =
        useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_OFFER_VALIDITY_DATE_ENABLED) &&
        !useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_FINANCE_DISABLED) &&
        hasFinanceVariants;
    const offerValidityDate = getFormattedOfferValidityDate(carDetails?.importInputs);
    const offerValidityDateString =
        showOfferValidityDate && offerValidityDate ? `${t('basket.offerValidityDate')} ${offerValidityDate}` : '';

    const isFinanceOverlayForcedToLink: boolean = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_FORCE_FINANCE_WIDGET_BUTTON,
        IFinanceWidgetButtonType.LINK
    );
    const isFinanceOverlayForcedToIcon: boolean = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_FORCE_FINANCE_WIDGET_BUTTON,
        IFinanceWidgetButtonType.ICON
    );
    const showFOLink =
        (financeDetailView === FINANCE_DETAIL_VIEW.LINK || isFinanceOverlayForcedToLink) &&
        !isFinanceOverlayForcedToIcon;
    const promoText =
        financeQuote?.primaryText ?? (carDetails?.extraFields?.promotionalText || carDetails?.promotionalText);

    return {
        t,
        formatMoney,
        filteredStockTrims,
        isPriceListLoading,
        carDetails,
        currentDeal,
        financeQuote,
        isFinance,
        router,
        totalBasicPriceWithoutVat,
        isStockJourney,
        isStockJourneyAllowed,
        isBasketLegalTermsEnabled,
        isSaveBasketCTADisabled,
        isStockBasketSaveItemEnabled,
        isBrandRecommendsJourneyAllowed,
        isAllowedRedirectToStock,
        isAllowedRedirectToMTO,
        isCataloguePriceFeatureSwitchEnabled,
        isFinanceWidgetDisabled,
        isCustomDepositTextAllowed,
        isBudgetChangeAllowed,
        FormattedDate,
        params,
        processCalculateSummaryDataPromise,
        cash,
        finance,
        catalog,
        currentPath,
        isBasketPage,
        showBasketLegalTerms,
        totalCashPrice,
        totalBasicPrice,
        totalMonthlyPrice,
        hasFinanceVariants,
        deposit,
        apr,
        redirectToStock,
        scrollToSockSlice,
        redirectToMTO,
        handleFinanceWidgetBtnClick,
        registerRenderCb,
        dispatch,
        showSaveButton,
        isCataloguePriceEqualToStorePrice,
        hideCashPayment,
        infoIconType,
        amountRounding,
        financeDetailView,
        IconName,
        financeDetailText,
        showOfferValidityDate,
        offerValidityDate,
        offerValidityDateString,
        isFinanceOverlayForcedToLink,
        isFinanceOverlayForcedToIcon,
        showFOLink,
        promoText,
    };
};
